/**
 * CashOutPage — citizen-side outlet pickup token issuance.
 *
 * Live (service-financial-services, citizen endpoints):
 *   - POST /v1/cash-out/request           — wallet debit + 6-digit token issued
 *   - GET  /v1/cash-out/tokens/me         — list citizen's tokens
 *   - POST /v1/cash-out/tokens/:id/cancel — cancel + refund wallet
 *
 * Test-mode flow (no real payout): wallet debited at request time, token
 * presented for 24 hours; cancel refunds. Bank-transfer rail remains
 * deferred (NIBSS not in scope here) — only the outlet pickup channel is
 * wired against the new tables.
 *
 * RBAC: CashOut/request-own + CashOut/read-own — granted to the `customer`
 * role by 20260509_nipost_pay_destub_perms.sql.
 */
import { useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight, Banknote, CheckCircle2, Loader2, X } from 'lucide-react';

import {
  cancelCashOutToken,
  listMyCashOutTokens,
  requestCashOut,
  type CashOutToken,
  type CashOutTokenWithBalance,
  type ListMyCashOutTokensResponse,
} from '../../lib/api/cash-out';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import {
  PageHeader,
  StatusBadge,
  customerWalletQueryKey,
  formatNaira,
  useCustomerWallet,
} from './NipostPayUI';

export function CashOutPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { token, claims } = useCustomerAuth();
  const customerId = claims?.upmpUserId ?? '';
  const wallet = useCustomerWallet(customerId, token);

  const [amount, setAmount] = useState<string>('');

  const tokensQuery = useQuery<ListMyCashOutTokensResponse, Error>({
    queryKey: ['cash-out-tokens', customerId] as const,
    queryFn: () => listMyCashOutTokens({ bearerToken: token ?? undefined }),
    enabled: token !== null,
    retry: false,
    staleTime: 30_000,
  });

  const issue = useMutation<CashOutTokenWithBalance, Error, undefined>({
    mutationFn: () => {
      if (!wallet.data) throw new Error('Wallet not loaded.');
      return requestCashOut(
        {
          amount: amount.length > 0 ? amount : '0',
          currency: wallet.data.currency,
          idempotencyKey: `cashout-${Date.now()}`,
        },
        { bearerToken: token ?? undefined },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerWalletQueryKey(customerId) });
      queryClient.invalidateQueries({ queryKey: ['cash-out-tokens', customerId] });
    },
  });

  const cancel = useMutation<CashOutTokenWithBalance, Error, string>({
    mutationFn: (id: string) =>
      cancelCashOutToken(id, { bearerToken: token ?? undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerWalletQueryKey(customerId) });
      queryClient.invalidateQueries({ queryKey: ['cash-out-tokens', customerId] });
    },
  });

  // eslint-disable-next-line upmp/no-number-money -- display-only insufficient-balance guard
  const balance = wallet.data ? Number(wallet.data.balanceCached ?? wallet.data.balance ?? '0') : 0;
  // eslint-disable-next-line upmp/no-number-money -- display-only insufficient-balance guard
  const requested = amount ? Number(amount) : 0;
  const insufficient = wallet.data && requested > balance;

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (requested <= 0 || insufficient) return;
    issue.mutate(undefined);
  }

  return (
    <div>
      <PageHeader title="Cash out" description="Withdraw funds from your wallet at any NIPOST outlet." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-border p-6">
            <p className="text-sm text-muted mb-1">Available to withdraw</p>
            {wallet.isLoading ? (
              <p className="text-sm text-muted inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </p>
            ) : wallet.data ? (
              <p className="text-3xl font-bold text-gray-900">
                {formatNaira(wallet.data.balanceCached ?? wallet.data.balance ?? '0')}
              </p>
            ) : null}
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-xl border border-border p-6 space-y-5"
            noValidate
          >
            <div>
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Banknote className="w-5 h-5 text-nipost-green" /> Outlet pickup
              </h3>
              <p className="text-sm text-muted mt-1">
                Enter the amount to withdraw. We'll issue a 6-digit token; bring it (with valid ID) to any NIPOST outlet within 24 hours.
              </p>
            </div>

            <div>
              <label htmlFor="cash-out-amount" className="block text-sm text-muted mb-1">
                Amount (₦)
              </label>
              <input
                id="cash-out-amount"
                type="number"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green"
              />
              {insufficient ? (
                <p className="mt-1 text-xs text-danger">Insufficient wallet balance.</p>
              ) : null}
            </div>

            {issue.isError ? (
              <div role="alert" className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                {issue.error.message}
              </div>
            ) : null}

            {issue.isSuccess ? (
              <div
                className="rounded-lg bg-success-light border border-success/20 px-3 py-2 text-sm text-success"
                role="status"
              >
                <div className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 className="w-4 h-4" /> Token issued
                </div>
                <div className="mt-1">
                  Your code:{' '}
                  <span className="font-mono text-lg tracking-wider" data-testid="cash-out-token">
                    {issue.data.token}
                  </span>
                </div>
                <div className="text-xs">Valid until {new Date(issue.data.expiresAt).toLocaleString('en-NG')}.</div>
              </div>
            ) : null}

            <button
              type="submit"
              disabled={
                requested <= 0 ||
                Boolean(insufficient) ||
                issue.isPending ||
                !wallet.data
              }
              className="w-full py-3 bg-nipost-green text-white font-semibold rounded-xl hover:bg-nipost-green-dark transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {issue.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
              Generate token{amount ? ` — ${formatNaira(amount)}` : ''}
            </button>
          </form>

          <div className="bg-white rounded-xl border border-border p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Active tokens</h3>
            {tokensQuery.isLoading ? (
              <p className="text-sm text-muted inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </p>
            ) : tokensQuery.data && tokensQuery.data.items.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted text-left border-b border-border">
                    <tr>
                      <th className="py-2">Token</th>
                      <th className="py-2">Amount</th>
                      <th className="py-2">Status</th>
                      <th className="py-2">Expires</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokensQuery.data.items.map((t: CashOutToken) => (
                      <tr key={t.id} className="border-b border-gray-50 last:border-0">
                        <td className="py-2 font-mono">{t.token}</td>
                        <td className="py-2 font-medium">{formatNaira(t.amount)}</td>
                        <td className="py-2"><StatusBadge status={t.status} /></td>
                        <td className="py-2 text-muted">
                          {new Date(t.expiresAt).toLocaleDateString('en-NG')}
                        </td>
                        <td className="py-2 text-right">
                          {t.status === 'PENDING' ? (
                            <button
                              type="button"
                              onClick={() => cancel.mutate(t.id)}
                              disabled={cancel.isPending}
                              className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1 disabled:opacity-50"
                            >
                              <X className="w-3.5 h-3.5" /> Cancel
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted">No tokens yet — generate one above.</p>
            )}
          </div>
        </div>

        <div>
          <div className="bg-white rounded-xl border border-border p-5 lg:sticky lg:top-24">
            <h3 className="font-semibold text-gray-900 mb-3">How it works</h3>
            <ul className="text-sm text-muted space-y-2">
              <li>1. Choose an amount and generate a token.</li>
              <li>2. Funds are held against your wallet.</li>
              <li>3. Visit any NIPOST outlet with valid ID and the token.</li>
              <li>4. The agent verifies and pays cash within 24 hours.</li>
              <li>5. Cancel any time before redemption to refund the hold.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
